import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Download, File as FileIcon, Loader2, Trash2, Pencil, Eye, Pin, FolderKanban } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { canEditEntry } from '@/lib/permissions';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DocPreviewDialog, canPreview } from '@/components/app/DocPreview';

const CATEGORY_LABEL = {
    meeting_report: 'Réunion', courrier: 'Courrier', contract: 'Contrat',
    invoice: 'Facture', report: 'Rapport', justificatif: 'Justificatif', autre: 'Autre',
};
const CLASSABLE = Object.keys(CATEGORY_LABEL);

const isPinned = (d) => Array.isArray(d.tags) && d.tags.includes('pinned');

const fmtSize = (b) => {
    if (!b) return '—';
    const u = ['B', 'KB', 'MB', 'GB'];
    let i = 0; while (b >= 1024 && i < u.length - 1) { b /= 1024; i++; }
    return `${b.toFixed(b >= 10 ? 0 : 1)} ${u[i]}`;
};

export default function GedGlobal() {
    const { user, profile } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [q, setQ] = useState('');
    const [catFilter, setCatFilter] = useState('all');
    const [projectFilter, setProjectFilter] = useState('all');

    const load = async () => {
        if (!currentWorkspace) return;
        setLoading(true);
        const { data, error: selErr } = await supabase
            .from('documents')
            .select('*, projects(id, name)')
            .eq('workspace_id', currentWorkspace.id)
            .or('category.is.null,category.neq.template')
            .order('created_at', { ascending: false });
        if (selErr) {
            setError(`Chargement impossible : ${selErr.message}`);
            setLoading(false);
            return;
        }
        setDocs(data || []);
        setLoading(false);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [currentWorkspace?.id]);

    const projects = [...new Map(docs.filter(d => d.projects).map(d => [d.projects.id, d.projects])).values()];

    const needle = q.trim().toLowerCase();
    const visible = docs.filter(d => {
        if (catFilter === 'none' && d.category) return false;
        if (catFilter !== 'all' && catFilter !== 'none' && d.category !== catFilter) return false;
        if (projectFilter !== 'all' && d.project_id !== projectFilter) return false;
        if (needle && !(d.name || '').toLowerCase().includes(needle)
            && !(d.projects?.name || '').toLowerCase().includes(needle)
            && !(CATEGORY_LABEL[d.category] || '').toLowerCase().includes(needle)) return false;
        return true;
    }).sort((a, b) => (isPinned(b) ? 1 : 0) - (isPinned(a) ? 1 : 0));

    const [preview, setPreview] = useState(null);

    const download = async (storage_path) => {
        const { data } = await supabase.storage.from('documents').createSignedUrl(storage_path, 300);
        if (data?.signedUrl) window.open(data.signedUrl, '_blank');
    };

    const classify = async (d, cat) => {
        const value = cat === '' ? null : cat;
        const { error: upErr } = await supabase.from('documents').update({ category: value }).eq('id', d.id);
        if (upErr) { toast.error(`Classement impossible : ${upErr.message}`); return; }
        setDocs(prev => prev.map(x => x.id === d.id ? { ...x, category: value } : x));
        await supabase.from('audit_logs').insert({
            workspace_id: currentWorkspace.id, user_id: user?.id, action: 'document.classified',
            entity_type: 'document', entity_id: d.id, new_value: { name: d.name, category: value },
        });
        toast.success(value ? `Classé : ${CATEGORY_LABEL[value]}` : 'Classement retiré');
    };

    const togglePin = async (d) => {
        const tags = Array.isArray(d.tags) ? d.tags : [];
        const pinned = tags.includes('pinned');
        const newTags = pinned ? tags.filter(t => t !== 'pinned') : [...tags, 'pinned'];
        const { error: upErr } = await supabase.from('documents').update({ tags: newTags }).eq('id', d.id);
        if (upErr) { toast.error(`Épinglage impossible : ${upErr.message}`); return; }
        setDocs(prev => prev.map(x => x.id === d.id ? { ...x, tags: newTags } : x));
        toast.success(pinned ? 'Document désépinglé' : 'Document épinglé en tête de liste');
    };

    const renameDoc = async (d) => {
        const input = window.prompt('Nouveau nom du document :', d.name);
        if (!input || !input.trim() || input.trim() === d.name) return;
        const name = input.trim();
        const { error: upErr } = await supabase.from('documents').update({ name }).eq('id', d.id);
        if (upErr) { toast.error(`Renommage impossible : ${upErr.message}`); return; }
        setDocs(prev => prev.map(x => x.id === d.id ? { ...x, name } : x));
        await supabase.from('audit_logs').insert({
            workspace_id: currentWorkspace.id, user_id: user?.id, action: 'document.renamed',
            entity_type: 'document', entity_id: d.id, old_value: { name: d.name }, new_value: { name },
        });
        toast.success('Document renommé');
    };

    const removeDoc = async (d) => {
        if (!window.confirm(`Supprimer le document « ${d.name} » ? Cette action est irréversible.`)) return;
        try { await supabase.storage.from('documents').remove([d.storage_path]); } catch { /* best effort */ }
        const { error: delErr } = await supabase.from('documents').delete().eq('id', d.id);
        if (delErr) { toast.error("Vous n'êtes pas autorisé·e à supprimer ce document."); return; }
        await supabase.from('audit_logs').insert({
            workspace_id: currentWorkspace.id, user_id: user?.id, action: 'document.deleted',
            entity_type: 'document', entity_id: d.id, old_value: { name: d.name },
        });
        setDocs(prev => prev.filter(x => x.id !== d.id));
        toast.success('Document supprimé');
    };

    return (
        <div className="space-y-6" data-testid="ged-global-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-heading text-4xl">GED Globale</h1>
                    <p className="text-muted-foreground text-sm mt-1">Tous les documents de l'espace, tous projets confondus.</p>
                </div>
                <div className="text-sm text-muted-foreground" data-testid="ged-global-count">{visible.length} / {docs.length} document(s)</div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[220px] max-w-md">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un document, un projet…"
                        className="pl-9" data-testid="ged-global-search" />
                </div>
                <select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}
                    className="h-9 px-2.5 rounded-md border border-border bg-background text-sm"
                    data-testid="ged-global-project-filter">
                    <option value="all">Tous les projets</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
            </div>

            {docs.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5" data-testid="ged-global-category-filter">
                    <button type="button" onClick={() => setCatFilter('all')}
                        className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${catFilter === 'all' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border text-foreground/70 hover:border-primary/40'}`}
                        data-testid="ged-global-filter-all">Tous ({docs.length})</button>
                    {CLASSABLE.filter(c => docs.some(d => d.category === c)).map(c => (
                        <button key={c} type="button" onClick={() => setCatFilter(c)}
                            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${catFilter === c ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border text-foreground/70 hover:border-primary/40'}`}
                            data-testid={`ged-global-filter-${c}`}>{CATEGORY_LABEL[c]} ({docs.filter(d => d.category === c).length})</button>
                    ))}
                    {docs.some(d => !d.category) && (
                        <button type="button" onClick={() => setCatFilter('none')}
                            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${catFilter === 'none' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border text-foreground/70 hover:border-primary/40'}`}
                            data-testid="ged-global-filter-none">Non classés ({docs.filter(d => !d.category).length})</button>
                    )}
                </div>
            )}

            {error && <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md" data-testid="ged-global-error">{error}</div>}

            <div className="card-soft overflow-x-auto" data-testid="ged-global-table">
                <table className="w-full">
                    <thead className="bg-surface-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-6 py-3">Nom</th>
                            <th className="px-6 py-3">Projet</th>
                            <th className="px-6 py-3">Catégorie</th>
                            <th className="px-6 py-3">Taille</th>
                            <th className="px-6 py-3">Ajouté le</th>
                            <th className="px-6 py-3"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {loading && <tr><td colSpan={6} className="px-6 py-8 text-center text-muted-foreground"><Loader2 className="inline animate-spin mr-2" size={14} /></td></tr>}
                        {!loading && docs.length === 0 && <tr><td colSpan={6} className="px-6 py-10 text-center text-foreground/60" data-testid="ged-global-empty">Aucun document dans l'espace. Importez des documents depuis l'onglet GED d'un projet.</td></tr>}
                        {!loading && docs.length > 0 && visible.length === 0 && <tr><td colSpan={6} className="px-6 py-10 text-center text-foreground/60" data-testid="ged-global-no-result">Aucun document ne correspond à la recherche.</td></tr>}
                        {!loading && visible.map((d) => {
                            const editable = canEditEntry({ entry: d, userId: user?.id, profile, currentWorkspace });
                            return (
                                <tr key={d.id} data-testid={`ged-global-row-${d.id}`} className={isPinned(d) ? 'bg-amber-50/40' : ''}>
                                    <td className="px-6 py-3 font-medium">
                                        <div className="flex items-center gap-2">
                                            {isPinned(d) ? <Pin size={14} className="text-amber-600 fill-amber-500 flex-shrink-0" /> : <FileIcon size={14} className="text-muted-foreground flex-shrink-0" />}
                                            <span className="line-clamp-1">{d.name}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-3 text-sm">
                                        {d.projects ? (
                                            <Link to={`/app/projects/${d.projects.id}`} className="inline-flex items-center gap-1.5 text-primary hover:underline" data-testid={`ged-global-project-link-${d.id}`}>
                                                <FolderKanban size={13} /> {d.projects.name}
                                            </Link>
                                        ) : <span className="text-muted-foreground">—</span>}
                                    </td>
                                    <td className="px-6 py-3">
                                        <select value={d.category || ''} onChange={(e) => classify(d, e.target.value)}
                                            className={`h-7 px-1.5 rounded-md border text-xs ${d.category ? 'border-secondary/40 bg-secondary-light text-secondary' : 'border-border bg-background text-muted-foreground'}`}
                                            data-testid={`ged-global-category-${d.id}`} title="Classer le document">
                                            <option value="">Classer…</option>
                                            {CLASSABLE.map(c => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
                                        </select>
                                    </td>
                                    <td className="px-6 py-3 text-sm">{fmtSize(d.size_bytes)}</td>
                                    <td className="px-6 py-3 text-sm">{new Date(d.created_at).toLocaleDateString('fr-FR')}</td>
                                    <td className="px-6 py-3 text-right whitespace-nowrap">
                                        <Button onClick={() => togglePin(d)} size="sm" variant="ghost" data-testid={`ged-global-pin-${d.id}`} title={isPinned(d) ? 'Désépingler' : 'Épingler en tête'}>
                                            <Pin size={14} className={isPinned(d) ? 'text-amber-600 fill-amber-500' : ''} />
                                        </Button>
                                        {canPreview(d.mime_type, d.name) && (
                                            <Button onClick={() => setPreview(d)} size="sm" variant="ghost" data-testid={`ged-global-preview-${d.id}`} title="Aperçu"><Eye size={14} /></Button>
                                        )}
                                        <Button onClick={() => download(d.storage_path)} size="sm" variant="ghost" data-testid={`ged-global-download-${d.id}`}><Download size={14} /></Button>
                                        {editable && (
                                            <Button onClick={() => renameDoc(d)} size="sm" variant="ghost" data-testid={`ged-global-rename-${d.id}`} title="Renommer"><Pencil size={13} /></Button>
                                        )}
                                        {editable && (
                                            <Button onClick={() => removeDoc(d)} size="sm" variant="ghost" data-testid={`ged-global-delete-${d.id}`}><Trash2 size={13} className="text-destructive" /></Button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <DocPreviewDialog doc={preview} onClose={() => setPreview(null)} onDownload={download} />
        </div>
    );
}
